/**
 * Organization guidance card — org-wide instructions that apply to every
 * member's AI agents, stored on the single `ahoy_agent_guidance` property of
 * the `ahoy_org` entity. Admins edit and save; non-admins see a read-only
 * view with copy explaining only admins can edit.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useUser } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { TextArea } from '@/lib/base/forms/textarea/textarea';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import type { EntityTypeName } from '@/lib/crm/core/types';
import { useSubmitEvents } from '@/lib/crm/query/mutations';
import { useCrmObject } from '@/lib/crm/store';
import { logger } from '@/lib/logger';

export function OrganizationGuidanceCard() {
  const { currentOrg } = useOrg();
  const user = useUser();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('ahoy_org', 'ahoy_property_updated');
  const orgEntity = useCrmObject(
    currentOrg?.id ?? 0,
    'ahoy_org' as EntityTypeName,
    currentOrg?.entityId ?? undefined
  );
  const { mutateAsync: submitEvents, isPending: isSaving } = useSubmitEvents(
    currentOrg?.id ?? 0,
    'ahoy_org' as EntityTypeName
  );
  const [formValue, setFormValue] = useState('');
  const [savedValue, setSavedValue] = useState('');
  const initializedOrgRef = useRef<number | null>(null);

  const raw = orgEntity.object?.properties?.ahoy_agent_guidance?.value;
  const currentValue = typeof raw === 'string' ? raw : '';

  // Sync CRM data → form state, mirroring MeetingAssistantSection: savedValue
  // always tracks canonical CRM state, but formValue is only re-initialized
  // on org change so an in-progress edit survives a WebSocket refetch.
  useEffect(() => {
    if (orgEntity.object) {
      setSavedValue(currentValue);
      const orgId = currentOrg?.id ?? null;
      if (initializedOrgRef.current !== orgId) {
        setFormValue(currentValue);
        initializedOrgRef.current = orgId;
      }
    }
  }, [orgEntity.object, currentOrg?.id, currentValue]);

  const handleChange = useCallback((value: string) => {
    setFormValue(value);
  }, []);

  const hasChanges = formValue !== savedValue;

  const handleSave = useCallback(async () => {
    if (!orgEntity.object || !canEdit) {
      return;
    }
    if (!user?.id) {
      toast.error('You must be signed in to save organization guidance');
      return;
    }

    try {
      const builder = createEventBuilder({
        sourceType: 'ahoy_user',
        sourceId: String(user.id),
        startSeq: orgEntity.object.last_event_seq,
      });
      builder.propertiesUpdated(orgEntity.object.entity_id, 'ahoy_org', {
        ahoy_agent_guidance: formValue,
      });
      await submitEvents({ events: builder.build() });
      toast.success('Organization guidance saved');
      setSavedValue(formValue);
    } catch (err) {
      logger.error(
        'Failed to save organization guidance',
        err instanceof Error ? err : new Error(String(err)),
        { org_id: currentOrg?.id, user_id: user?.id }
      );
      toast.error("We couldn't save the organization guidance. Try again.");
      setFormValue(savedValue);
    }
  }, [
    formValue,
    savedValue,
    orgEntity.object,
    submitEvents,
    user,
    canEdit,
    currentOrg?.id,
  ]);

  const handleRetry = useCallback(() => {
    void orgEntity.refetch();
  }, [orgEntity]);

  if (!currentOrg?.entityId) {
    return null;
  }

  if (orgEntity.isLoading) {
    return (
      <div>
        <h3 className='text-base font-semibold text-fg-primary'>
          Organization guidance
        </h3>
        <p className='text-sm text-fg-tertiary mt-0.5 mb-2'>
          Set org-wide instructions that apply to every member&apos;s AI agents.
        </p>
        <p className='text-sm text-fg-tertiary'>Loading…</p>
      </div>
    );
  }

  // No object is available (initial fetch failed, or the entity could not be
  // loaded). Without this branch admins would see an editable blank card whose
  // Save silently no-ops (handleSave returns early when object is missing),
  // making them think a save succeeded when nothing was persisted. Gated on the
  // missing object alone — NOT on `error` — because `useCrmObject` keeps the
  // store object live while surfacing the latest refetch error, so a transient
  // background-refetch failure must not blank out already-loaded guidance.
  if (!orgEntity.object) {
    return (
      <div>
        <h3 className='text-base font-semibold text-fg-primary'>
          Organization guidance
        </h3>
        <p className='text-sm text-fg-tertiary mt-0.5 mb-2'>
          Set org-wide instructions that apply to every member&apos;s AI agents.
        </p>
        <p className='text-sm text-error-primary'>
          Couldn&apos;t load organization guidance. Check your connection and
          try again.
        </p>
        <div className='mt-3 flex justify-end'>
          <Button
            onClick={handleRetry}
            isLoading={orgEntity.isFetching}
            color='secondary'
            size='sm'
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div>
        <h3 className='text-base font-semibold text-fg-primary'>
          Organization guidance
        </h3>
        <p className='text-sm text-fg-tertiary mt-0.5 mb-2'>
          Set org-wide instructions that apply to every member&apos;s AI agents.
        </p>
        {savedValue ? (
          <p className='text-sm text-fg-primary whitespace-pre-wrap'>
            {savedValue}
          </p>
        ) : (
          <p className='text-sm text-fg-tertiary'>
            No organization guidance has been set.
          </p>
        )}
        <p className='text-sm text-fg-tertiary mt-2'>
          Only account admins can change organization guidance. Ask an admin if
          this needs updating.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h3 className='text-base font-semibold text-fg-primary'>
        Organization guidance
      </h3>
      <p className='text-sm text-fg-tertiary mt-0.5 mb-2'>
        Set org-wide instructions that apply to every member&apos;s AI agents.
      </p>
      <TextArea
        value={formValue}
        onChange={handleChange}
        rows={4}
        className='mt-2'
        maxLength={10000}
        placeholder='e.g., Always use British English; never discuss pricing without approval.'
        isDisabled={isSaving}
      />
      <div className='mt-3 flex justify-end'>
        <Button
          onClick={handleSave}
          isDisabled={!hasChanges || isSaving || !user?.id}
          isLoading={isSaving}
          color='primary'
          size='sm'
        >
          Save
        </Button>
      </div>
    </div>
  );
}
